import { Link, useLocation } from 'react-router-dom';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './FastApplication.css';
import Footer from './Footer';
import Header from './Header';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Helmet } from "react-helmet-async";


const DIAL_CODES = ["+382", "+385", "+387", "+381", "+386", "+389"];

// Local number constraints
const MAX_LOCAL_DIGITS = 9;
const MIN_LOCAL_DIGITS = 8; // adjust if you want exactly 9 -> set both to 9
// ---- Sanitizers (unchanged) ----
const sanitizers = {
  fullName: (val) => val.replace(/[^a-zA-Z\s]/g, '').slice(0, 45),
  localPhone: (val) => val.replace(/\D/g, '').slice(0, MAX_LOCAL_DIGITS),

  // NEW: dropdown value – leave as is
  dialCode: (val) => val,
  salaryExpectations: (val) => val.replace(/\D/g, '').slice(0, 4),
  startDate: (val) => {
    const dateParts = val.split('-');
    if (dateParts[0] && dateParts[0].length > 4) {
      dateParts[0] = dateParts[0].slice(0, 4);
    }
    return dateParts.join('-');
  },
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

function FastApplication() {


  const [formData, setFormData] = useState({
    fullName: '',
    salaryExpectations: '',
    dialCode: '+382',      // default selection
    localPhone: '',        // 6–9 digits (max 9)
    startDate: '',
    location: '',
  });


  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const validateField = (field, value) => {
    let error = '';

    switch (field) {
      case 'fullName':
        if (!value.trim()) error = 'Ime i prezime je obavezno.';
        break;
      case 'dialCode':
        if (!value) error = 'Odaberite pozivni broj.';
        break;

      // NEW
      case 'localPhone': {
        const re = new RegExp(`^\\d{${MIN_LOCAL_DIGITS},${MAX_LOCAL_DIGITS}}$`);
        if (!re.test(value)) error = `Unesite važeći broj (${MIN_LOCAL_DIGITS}–${MAX_LOCAL_DIGITS} cifara).`;
        break;
      }
      case 'salaryExpectations':
        if (!value) error = 'Plata je obavezna.';
        break;
      case 'startDate':
        if (!value) error = 'Datum početka rada je obavezan.';
        break;
      case 'location':
        if (!value) error = 'Lokacija je obavezna.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const fileInputRef = useRef(null);
  const location = useLocation();

  const [positions, setPositions] = useState([]);
  const [locations, setLocations] = useState([]);
  // const [selectedPositions, setSelectedPositions] = useState(['']);
  const [selectedPositions, setSelectedPositions] = useState(() => {
    const jobTitle = location.state?.jobTitle || '';
    return jobTitle ? [jobTitle] : [''];
  });


  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadedDocument, setUploadedDocument] = useState(null);

  const [positionSearch, setPositionSearch] = useState('');
  const [dropdownOpenIndex, setDropdownOpenIndex] = useState(null);

  const sanitizeSearchInput = (value) => {
    return value.replace(/[^a-zA-ZćčžšđĆČŽŠĐ\s]/g, ''); // allows only letters and spaces (with local letters)
  };



  //const dropdownRef = useRef(null);
  const dropdownRefs = useRef([]);

  const [submitting, setSubmitting] = useState(false);



  // Close dropdown
  const closeDropdown = useCallback(() => setDropdownOpenIndex(null), []);

  // Toggle one dropdown
  const handleDropdownToggle = (index) => {
    setDropdownOpenIndex(index === dropdownOpenIndex ? null : index);
    setPositionSearch('');
  };

  // Outside click / Escape closes dropdown
  useEffect(() => {
    const onDocClick = (e) => {
      if (
        dropdownOpenIndex !== null &&
        dropdownRefs.current[dropdownOpenIndex] &&
        !dropdownRefs.current[dropdownOpenIndex].contains(e.target)
      ) {
        closeDropdown();
      }
    };

    const onEsc = (e) => {
      if (e.key === 'Escape') closeDropdown();
    };

    document.addEventListener('mousedown', onDocClick); // 👈 change here
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick); // 👈 and here
      document.removeEventListener('keydown', onEsc);
    };
  }, [dropdownOpenIndex, closeDropdown]);


  // Fetch options
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [positionsRes, locationsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/positions`),
          fetch(`${API_BASE_URL}/locations`),
        ]);

        if (!positionsRes.ok || !locationsRes.ok) {
          throw new Error("Failed to fetch positions or locations");
        }

        const [positionsData, locationsData] = await Promise.all([
          positionsRes.json(),
          locationsRes.json(),
        ]);

        setPositions(positionsData);
        setLocations(locationsData);
      } catch (err) {
        console.error("Error fetching data:", err);
        toast.error("Greška pri učitavanju podataka. Pokušajte ponovo.");
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Controlled inputs w/ sanitizers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: sanitizers[name] ? sanitizers[name](value) : value,
    }));
  };

  const handlePositionChange = (index, value) => {
    const updated = [...selectedPositions];
    updated[index] = value;
    setSelectedPositions(updated);
    setDropdownOpenIndex(null);
  };

  const addPosition = () => {
    if (selectedPositions.length >= 2) {
      toast.warning("Možete dodati samo još jednu poziciju.");
      return;
    }
    setSelectedPositions((prev) => [...prev, '']);
  };

  const removePosition = (index) =>
    setSelectedPositions((prev) => prev.filter((_, i) => i !== index));

  // const handleDocumentChange = (e) => setUploadedDocument(e.target.files[0]);

  const handleDocumentChange = (e) => {
    const file = e.target.files[0];

    // ✅ If no file selected, reset state
    if (!file) {
      setUploadedDocument(null);
      return;
    }

    // ✅ 1. Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dokument je prevelik. Maksimalna veličina je 5MB.');
      e.target.value = ''; // Clear invalid file
      setUploadedDocument(null);
      return;
    }

    // ✅ 2. Check allowed file types
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Dozvoljeni su samo PDF, DOC i DOCX fajlovi.');
      e.target.value = ''; // Clear invalid file
      setUploadedDocument(null);
      return;
    }

    // ✅ 3. If valid, store file
    setUploadedDocument(file);
  };



  const isFormValid = () => {
    const phoneOk =
      formData.dialCode &&
      new RegExp(`^\\d{${MIN_LOCAL_DIGITS},${MAX_LOCAL_DIGITS}}$`).test(formData.localPhone);
    return (
      formData.fullName &&
      phoneOk &&
      formData.salaryExpectations &&
      formData.startDate &&
      formData.location &&
      selectedPositions.length > 0 &&
      uploadedDocument
    );
  };

  const handleUploadDocument = async () => {

    // if (submitting) return; // Prevent duplicate uploads
    //   setSubmitting(true);

    // Native form validation
    const formElement = document.querySelector('.fast-app-form');
    if (!formElement.checkValidity()) {
      const firstInvalid = formElement.querySelector(':invalid');
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalid.focus();
      }
      return;
    }

    // Phone validation
    // Phone validation (before sending)
    const phoneOk =
      formData.dialCode &&
      new RegExp(`^\\d{${MIN_LOCAL_DIGITS},${MAX_LOCAL_DIGITS}}$`).test(formData.localPhone);

    if (!phoneOk) {
      const phoneInput = document.querySelector('input[name="localPhone"]');
      if (phoneInput) {
        phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        phoneInput.focus();
      }
      return;
    }


    // Ensure first position selected
    if (!selectedPositions[0] || selectedPositions[0].trim() === '') {
      const firstRow = document.querySelector('.position-row');
      if (firstRow) {
        firstRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstRow.focus();
      }
      return;
    }

    const [firstPosition, ...extraPositions] = selectedPositions;
    const send = new FormData();
    send.append('fullName', formData.fullName);
    // Combine for server
    const combinedPhone = `${formData.dialCode}${formData.localPhone}`;
    send.append('phoneNumber', combinedPhone);

    send.append('salaryExpectations', formData.salaryExpectations);
    send.append('startDate', formData.startDate);
    send.append('location', formData.location);
    send.append('applyingForPosition', firstPosition);
    send.append('extraposition', extraPositions.join(', '));
    send.append('document', uploadedDocument);


    try {
      // const response = await fetch('http://localhost:5000/upload-document', {
      //   method: 'POST',
      //   body: send,
      // });
      const response = await fetch(`${API_BASE_URL}/upload-document`, {
        method: 'POST',
        body: send,
      });

      if (!response.ok) {
        console.error(`Upload failed: ${response.status} ${response.statusText}`);
        toast.error('Greška na serveru. Pokušajte ponovo.');
        return;
      }

      const result = await response.json();


      if (result.success) {
        toast.success('Dokument je uspješno poslat!');
        setFormData({
          fullName: '',
          salaryExpectations: '',
          dialCode: '+382',   // default again
        localPhone: '',
          startDate: '',
          location: '',
        });
        setSelectedPositions(['']);
        setUploadedDocument(null);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        toast.error('Greška pri slanju dokumenta. Pokušajte ponovo.');
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      toast.error('Došlo je do greške pri slanju dokumenta. Pokušajte ponovo.');
    }

  };

  return (
    <>

      <Helmet>
        <title>Brza Prijava za Posao | Sezonski Poslovi Star</title>
        <meta
          name="description"
          content="Popunite brzu prijavu za posao i pošaljite svoj CV. Sezonski Poslovi Star vam pomaže da pronađete posao brzo i lako."
        />
        <meta
          name="keywords"
          content="brza prijava, posao, karijera, CV, zapošljavanje, Sezonski Poslovi Star"
        />
        <meta property="og:title" content="Brza Prijava za Posao | Sezonski Poslovi Star" />
        <meta
          property="og:description"
          content="Brza prijava za posao! Pošaljite svoj CV i aplicirajte za više pozicija uz samo nekoliko klikova."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.starrecruitment.com/fast-application" />
        <meta property="og:image" content="/images/seo-banner.jpg" />
      </Helmet>

      <Header />

      <div className="fastApp-wrapper">
        <div className="fastApplication">
          <form
            className="fast-app-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleUploadDocument();
            }}
            noValidate
          >
            <div className="application-section">
              <h2>PRIJAVA ZA POSAO</h2>

              {location.state?.jobTitle && (
                <div className="job-title-banner">
                  <p><strong>POZICIJA:</strong> {location.state.jobTitle}</p>
                </div>
              )}


              {/* Full Name */}
              <div className="section-fastApplication">
                <h3>Ime i prezime:</h3>
                <input
                  type="text"
                  name="fullName"
                  placeholder="Npr: Marko Markovic"
                  value={formData.fullName}
                  onChange={handleChange}
                  onBlur={() => handleBlur('fullName')}
                  className={touched.fullName && errors.fullName ? 'input-error' : ''}
                  required
                  aria-label="Full name"
                />
                {touched.fullName && errors.fullName && (
                  <div className="field-error">{errors.fullName}</div>
                )}
              </div>

              {/* Positions */}
<div className="section-fastApplication">
  <h3>Prijavljujem se za sledeće pozicije:</h3>

  {location.state?.jobTitle ? (
    <>
      {/* Locked first position */}
      <div className="position-row">
        <input
          type="text"
          value={selectedPositions[0]}
          readOnly
          className="position-dropdown locked-position"
        />
      </div>

      {/* Allow one extra position if not already added */}
      {selectedPositions.length < 2 && (
        <div className="position-actions">
          <button
            type="button"
            onClick={addPosition}
            className="add-button"
          >
            Dodaj još jednu poziciju
          </button>
        </div>
      )}

      {/* If extra position is added → render dropdown for it */}
      {selectedPositions.length === 2 && (
        <div className="position-row">
          <div
            className="dropdown-container"
            onClick={(e) => e.stopPropagation()}
            ref={(el) => (dropdownRefs.current[1] = el)}
          >
            <div
              className="position-dropdown"
              role="button"
              tabIndex={0}
              onClick={() => handleDropdownToggle(1)}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') && handleDropdownToggle(1)
              }
              aria-haspopup="listbox"
              aria-expanded={dropdownOpenIndex === 1}
            >
              {selectedPositions[1] || 'Izaberite dodatnu poziciju'}
            </div>

            {dropdownOpenIndex === 1 && (
              <div className="dropdown-menu" role="listbox">
                <input
                  type="text"
                  placeholder="Pretražite pozicije..."
                  value={positionSearch}
                  onChange={(e) =>
                    setPositionSearch(
                      sanitizeSearchInput(e.target.value.toLowerCase())
                    )
                  }
                  className="dropdown-search-input"
                  aria-label="Pretražite pozicije"
                  autoFocus
                />
                {positions
                  .filter((p) => p.name.toLowerCase().includes(positionSearch))
                  .map((p) => (
                    <div
                      key={p.id}
                      className="dropdown-item"
                      role="option"
                      onMouseDown={() => handlePositionChange(1, p.name)}
                    >
                      {p.name}
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div className="position-actions">
            <button
              type="button"
              onClick={() => removePosition(1)}
              className="remove-button"
            >
              Obriši poziciju
            </button>
          </div>
        </div>
      )}
    </>
  ) : (
    // Original dropdowns when not coming from JobDetailsPage
    selectedPositions.map((position, index) => (
      <div key={index} className="position-row">
        <div
          className="dropdown-container"
          onClick={(e) => e.stopPropagation()}
          ref={(el) => (dropdownRefs.current[index] = el)}
        >
          <div
            className="position-dropdown"
            role="button"
            tabIndex={0}
            onClick={() => handleDropdownToggle(index)}
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') && handleDropdownToggle(index)
            }
            aria-haspopup="listbox"
            aria-expanded={dropdownOpenIndex === index}
          >
            {position || 'Izaberite poziciju'}
          </div>

          {dropdownOpenIndex === index && (
            <div className="dropdown-menu" role="listbox">
              <input
                type="text"
                placeholder="Pretražite pozicije..."
                value={positionSearch}
                onChange={(e) =>
                  setPositionSearch(
                    sanitizeSearchInput(e.target.value.toLowerCase())
                  )
                }
                className="dropdown-search-input"
                aria-label="Pretražite pozicije"
                autoFocus
              />
              {positions
                .filter((p) => p.name.toLowerCase().includes(positionSearch))
                .map((p) => (
                  <div
                    key={p.id}
                    className="dropdown-item"
                    role="option"
                    onMouseDown={() => handlePositionChange(index, p.name)}
                  >
                    {p.name}
                  </div>
                ))}
            </div>
          )}
        </div>

        <div className="position-actions">
          {index > 0 && (
            <button
              type="button"
              onClick={() => removePosition(index)}
              className="remove-button"
            >
              Obriši poziciju
            </button>
          )}
          {index === selectedPositions.length - 1 &&
            selectedPositions.length < 2 && (
              <button
                type="button"
                onClick={addPosition}
                className="add-button"
              >
                Dodaj poziciju
              </button>
            )}
        </div>
      </div>
    ))
  )}
</div>


              {/* Phone */}
              <div className="section-fastApplication">
                <h3>Broj telefona:</h3>

                <div className="phone-row">
                  <select
                    name="dialCode"
                    value={formData.dialCode}
                    onChange={handleChange}
                    onBlur={() => handleBlur('dialCode')}
                    className={`phone-code ${touched.dialCode && errors.dialCode ? 'input-error' : ''}`}
                    required
                    aria-label="Pozivni broj"
                  >
                    <option value="" disabled>+ Pozivni</option>
                    {DIAL_CODES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>

                  <input
                    type="tel"
                    inputMode="numeric"
                    name="localPhone"
                    placeholder="npr. 68123456"
                    value={formData.localPhone}
                    onChange={handleChange}
                    onBlur={() => handleBlur('localPhone')}
                    className={`${touched.localPhone && errors.localPhone ? 'input-error' : ''}`}
                    maxLength={MAX_LOCAL_DIGITS}
                    pattern={`\\d{${MIN_LOCAL_DIGITS},${MAX_LOCAL_DIGITS}}`}
                    required
                    aria-label="Broj telefona bez pozivnog"
                  />
                </div>

                {touched.dialCode && errors.dialCode && (
                  <div className="field-error">{errors.dialCode}</div>
                )}
                {touched.localPhone && errors.localPhone && (
                  <div className="field-error">{errors.localPhone}</div>
                )}

                <small className="phone-hint">
                  Unesite {MIN_LOCAL_DIGITS}–{MAX_LOCAL_DIGITS} cifara bez pozivnog (max {MAX_LOCAL_DIGITS}).
                </small>
              </div>

              {/* Start date */}
              <div className="section-fastApplication">
                <h3>Početak rada:</h3>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  onBlur={() => handleBlur('startDate')}
                  className={touched.startDate && errors.startDate ? 'input-error' : ''}
                  required
                  aria-label="Pocetak Rada"
                />
                {touched.startDate && errors.startDate && (
                  <div className="field-error">{errors.startDate}</div>
                )}
              </div>

              {/* Location */}
              <div className="section-fastApplication">
                <h3>Mjesto rada:</h3>
                {loading ? (
                  <p>Loading locations...</p>
                ) : error ? (
                  <p style={{ color: 'red' }}>{error}</p>
                ) : (
                  <select
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    onBlur={() => handleBlur('location')}
                    required
                    aria-label="Preferred location"
                    className={touched.location && errors.location ? 'input-error' : ''}
                  >
                    <option value="" disabled>
                      Izaberite mjesto
                    </option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.location}>
                        {loc.location}
                      </option>
                    ))}
                  </select>
                )}
                {touched.location && errors.location && (
                  <div className="field-error">{errors.location}</div>
                )}
              </div>

              {/* Salary */}
              <div className="section-fastApplication">
                <h3>Očekivana mjesečna plata ( U Eurima ):</h3>
                <input
                  type="text"
                  name="salaryExpectations"
                  placeholder="Npr: 1200"
                  value={formData.salaryExpectations}
                  onChange={handleChange}
                  onBlur={() => handleBlur('salaryExpectations')}
                  className={touched.salaryExpectations && errors.salaryExpectations ? 'input-error' : ''}
                  required
                  aria-label="Očekivana Plata"
                />
                {touched.salaryExpectations && errors.salaryExpectations && (
                  <div className="field-error">{errors.salaryExpectations}</div>
                )}
              </div>

              {/* Document */}
              <div className="section-fastApplication">
                <h3>Dodajte dokumenat ( CV, Rezime ):</h3>
                <input
                  type="file"
                  name="document"
                  accept=".pdf,.docx,.doc"
                  onChange={handleDocumentChange}
                  required
                  aria-label="Upload document"
                  ref={fileInputRef}
                />
              </div>

              {/* Actions */}
              <div className="actions-row">
                <button type="submit" className="primary-btn" disabled={!isFormValid()}>
                  POŠALJI PRIJAVU ZA POSAO
                </button>

                <Link to="/create-cv" className="link-reset">
                  <button type="button" className="secondary-btn">NAPRAVI CV / REZIME</button>
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>

      <Footer />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

    </>
  );
}

export default FastApplication;